import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { event } from 'jquery';
import { useAllUsers } from 'context/AllUserDataContext';
import useConfirmationDialog from 'routes/Pages/components/ConfirmDialogs/useConfirmationDialog';
import useUrlParam from 'services/globalHooks/useUrlParam';

const useHfOpsTaskController = () => {
  const [hfOpsData, setHfOpsData] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const { showToast } = useToast();
  const {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,
  } = useConfirmationDialog();

  const { getParam, setParam, deleteParam } = useUrlParam();

  //openEdit dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedHfTask, setSelectedHfTask] = useState(null);

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const handleOpenAddDialogClick = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // ── Startup & Deep Linking ────────────────────────────────────────────

  // Effect 1: Initial load
  useEffect(() => {
    isMounted.current = true;
    getHFOpsData();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Effect 2: Deep Linking (Triggered when hfOpsData changes)
  useEffect(() => {
    const taskIdFromUrl = getParam('taskID');
    if (taskIdFromUrl && !openEditDialog && hfOpsData.length > 0) {
      const taskToOpen = hfOpsData.find(task => String(task.id) === String(taskIdFromUrl));
      if (taskToOpen) {
        // Use a functional wrapper to make sure state setter picks up latest data
        setOpenEditDialog(true);
        setSelectedHfTask(taskToOpen);
      }
    }
  }, [hfOpsData, getParam, openEditDialog]);

  // ── Fetch HF Ops Data ─────────────────────────────────────────────────

  const getHFOpsData = () => {
    dispatch({ type: FETCH_START });
    let url = '/Ops/GetTasks';

    customAxios
      .get(url, { headers: authHeader() })
      .then(data => {
        if (data.status == 200) {
          setHfOpsData(data.data);
        } else {
          showToast('Failed to load data Purchase Requisition Data ', 'error');
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(error => {
        showToast('Failed to load data Purchase Requisition Data ', 'error');
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  // ── Tab Change ────────────────────────────────────────────────────────

  const handleTabChange = (event, val) => {
    setCurrentTab(val);
  };

  // ── Open Edit Dialog ──────────────────────────────────────────────────

  const handleOpeneditDialogClick = taskData => {
    setOpenEditDialog(true);
    setSelectedHfTask(taskData);
    // Update URL with taskID
    setParam('taskID', taskData.id);
  };

  // ── Close Edit Dialog ─────────────────────────────────────────────────

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedHfTask(null);
    // Remove taskID from URL
    deleteParam('taskID');
  };

  // ── Handle Successful Submit ──────────────────────────────────────────

  const handleSuccessfulSubmit = () => {
    handleCloseEditDialog(); // This will close dialog and remove URL param
    getHFOpsData();
    showToast('Successfully Updated', 'success');
  };

  const handleDeleteHfTask = task => {
    handleOpenConfirmationDialog(
      `Are you sure you want to delete task #${task.id}?`,
      () => confirmDeleteHfTask(task.id),
    );
  };

  const confirmDeleteHfTask = async id => {
    dispatch({ type: FETCH_START });
    try {
      // Replace with your real delete endpoint
      const res = await customAxios.post(`/Ops/DeleteTask`, {taskId: id}, { headers: authHeader() });

      if (res.status === 200 || res.status === 204) {
        showToast('Task deleted successfully', 'success');
        getHFOpsData(); // refresh grid
      } else {
        showToast('Failed to delete task', 'error');
      }
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      console.error('Delete failed', err);
      showToast('Failed to delete task', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    } finally {
      closeConfirmationDialog();
    }
  };

  return {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,
    hfOpsData,
    setHfOpsData,
    handleTabChange,
    currentTab,
    handleOpeneditDialogClick,
    selectedHfTask,
    openEditDialog,
    setOpenEditDialog,
    handleCloseEditDialog, // Export this instead of setOpenEditDialog
    handleSuccessfulSubmit,
    openAddDialog,
    handleOpenAddDialogClick,
    handleCloseAddDialog,
    handleDeleteHfTask
  };
};

export default useHfOpsTaskController;
